import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import InputMask from "react-input-mask";
import DatePicker from "react-datepicker";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "react-datepicker/dist/react-datepicker.css";

const schema = yup.object().shape({
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().required("Last name is required"),
  gender: yup.string().required("Gender is required"),
  date_of_birth: yup
    .date()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .notRequired(),
  phone: yup
    .string()
    .matches(/^\d{3}-\d{3}-\d{4}$/, "Phone number is not valid")
    .required("Phone is required"),
});

const GeneralInfoForm = () => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [avatarPreview, setAvatarPreview] = useState("/path/to/default.jpg");
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const fileInputRef = useRef();

  const onSubmit = (data) => {
    console.log("Submitted data:", data);
    // API call or state update here
  };

  const onReset = () => {
    reset();
    setDateOfBirth(null);
    setAvatarPreview("/path/to/default.jpg");
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="flex items-center gap-4">
        <img
          src={avatarPreview}
          alt="Profile"
          className="rounded w-20 h-20 object-cover"
        />
        <div>
          <button
            type="button"
            onClick={() => fileInputRef.current.click()}
            className="btn btn-primary btn-sm"
          >
            Upload
          </button>
          <input
            type="file"
            ref={fileInputRef}
            hidden
            accept="image/*"
            onChange={handleFileChange}
          />
          <p className="text-sm text-gray-500">Allowed JPG, PNG, GIF. Max 800KB</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block">First Name</label>
          <input
            {...register("first_name")}
            className={`form-input w-full ${errors.first_name ? "border-red-500" : ""}`}
            placeholder="Firstname"
          />
          <p className="text-red-500 text-sm">{errors.first_name?.message}</p>
        </div>

        <div>
          <label className="block">Last Name</label>
          <input
            {...register("last_name")}
            className={`form-input w-full ${errors.last_name ? "border-red-500" : ""}`}
            placeholder="Lastname"
          />
          <p className="text-red-500 text-sm">{errors.last_name?.message}</p>
        </div>

        <div>
          <label className="block">Gender</label>
          <select
            {...register("gender")}
            className={`form-select w-full ${errors.gender ? "border-red-500" : ""}`}
          >
            <option value="">Select your gender</option>
            <option value="0">Male</option>
            <option value="1">Female</option>
            <option value="2">Other</option>
          </select>
          <p className="text-red-500 text-sm">{errors.gender?.message}</p>
        </div>

        <div>
          <label className="block">Date of Birth</label>
          <DatePicker
            selected={dateOfBirth}
            onChange={(date) => {
              setDateOfBirth(date);
              setValue("date_of_birth", date);
            }}
            className={`form-input w-full ${errors.date_of_birth ? "border-red-500" : ""}`}
            placeholderText="Select date"
            dateFormat="MM/dd/yyyy"
          />
          <p className="text-red-500 text-sm">{errors.date_of_birth?.message}</p>
        </div>

        <div>
          <label className="block">Phone</label>
          <InputMask
            mask="999-999-9999"
            {...register("phone")}
            className={`form-input w-full ${errors.phone ? "border-red-500" : ""}`}
            placeholder="Phone"
          />
          <p className="text-red-500 text-sm">{errors.phone?.message}</p>
        </div>
      </div>

      <div className="flex gap-2">
        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          Save changes
        </button>
        <button type="button" className="btn btn-danger" onClick={onReset}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default GeneralInfoForm;
